import React, { useCallback, useEffect, useState } from "react";
import { Button, Space, Spin, Typography, message } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import DocumentPreviewModal from "../BookingsAdmin/DocumentPreviewModal";
import {
  getMasterContainerDocument,
  type MasterContainerDocument,
} from "../../services/masterContainerDocumentsService";
import {
  downloadContainerDocument,
  getContainerDocumentPreviewUrl,
  getFileType,
} from "../../utils/containerDocumentsUtils";

const { Text } = Typography;

export interface ContainerBlNumberDisplayProps {
  masterContainerId?: number | null;
  masterReference?: string | null;
  /** Legacy per-container B/L when no master is linked. */
  fallbackBlNumber?: string | null;
  loading?: boolean;
}

/**
 * Shows master B/L reference and/or uploaded AWB/BL file in the container header.
 */
const ContainerBlNumberDisplay: React.FC<ContainerBlNumberDisplayProps> = ({
  masterContainerId,
  masterReference,
  fallbackBlNumber,
  loading: parentLoading,
}) => {
  const [document, setDocument] = useState<MasterContainerDocument | null>(
    null
  );
  const [docLoading, setDocLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const fetchDocument = useCallback(async () => {
    if (!masterContainerId) {
      setDocument(null);
      return;
    }
    setDocLoading(true);
    const result = await getMasterContainerDocument(masterContainerId);
    if (result.error) {
      message.error("Failed to load B/L document");
      setDocument(null);
    } else {
      setDocument(result.data);
    }
    setDocLoading(false);
  }, [masterContainerId]);

  useEffect(() => {
    void fetchDocument();
  }, [fetchDocument]);

  const handlePreview = async () => {
    if (!document) return;
    const fileInfo = getFileType(document.file_name);
    if (fileInfo.type === "other") {
      await downloadContainerDocument(document.file_path, document.file_name);
      return;
    }
    setPreviewVisible(true);
    const url = await getContainerDocumentPreviewUrl(document.file_path);
    if (!url) {
      message.error("Failed to load preview");
      setPreviewVisible(false);
      return;
    }
    setPreviewUrl(url);
  };

  if (parentLoading || (masterContainerId && docLoading)) {
    return <Spin size="small" />;
  }

  if (!masterContainerId) {
    return <span>{fallbackBlNumber || "-"}</span>;
  }

  const displayName =
    masterReference?.trim() || `B/L #${masterContainerId}`;

  return (
    <>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Button
          type="link"
          onClick={() => {
            window.location.href = `/admin/containers?tab=master-containers&master_id=${masterContainerId}&action=manage`;
          }}
          style={{ padding: 0, height: "auto", fontWeight: "bold" }}
        >
          {displayName}
        </Button>
        {document ? (
          <Space size={4} wrap>
            <FileTextOutlined style={{ color: "#1890ff" }} />
            <Text
              ellipsis
              title={document.file_name}
              style={{ maxWidth: 220, fontSize: 12 }}
            >
              {document.file_name}
            </Text>
            {getFileType(document.file_name).type !== "other" && (
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => void handlePreview()}
                style={{ padding: 0, height: "auto" }}
              />
            )}
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() =>
                void downloadContainerDocument(
                  document.file_path,
                  document.file_name
                )
              }
              style={{ padding: 0, height: "auto" }}
            />
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No B/L file uploaded
          </Text>
        )}
      </Space>

      <DocumentPreviewModal
        visible={previewVisible}
        onClose={() => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewVisible(false);
          setPreviewUrl(null);
        }}
        fileName={document?.file_name || ""}
        blobUrl={previewUrl}
        onDownload={
          document
            ? () =>
                downloadContainerDocument(
                  document.file_path,
                  document.file_name
                )
            : undefined
        }
      />
    </>
  );
};

export default ContainerBlNumberDisplay;
